import { ComboboxField, TextField } from "@/components/site/fields";
import {
  addressFormat,
  postalCodeError,
  type AddressLocale,
  type AddressValues,
} from "@/lib/address";
import { countryOptions } from "@/lib/countries";

const countryList = countryOptions(["ES", "SE", "NO", "DK", "GB"]);

export type AddressErrors = Partial<Record<keyof AddressValues, string | undefined>>;

/**
 * Bostadslandet väljs först och styr etiketter, format och regionlista på
 * fälten under. Resten av adressen visas först när landet är valt – inga
 * tomma fält innan vi vet vilket format som gäller.
 */
export function AddressFields({
  values,
  onChange,
  disabled,
  errors,
  locale = "sv",
}: {
  values: AddressValues;
  onChange: (patch: Partial<AddressValues>) => void;
  disabled?: boolean;
  errors?: AddressErrors;
  locale?: AddressLocale;
}) {
  const format = addressFormat(values.country, locale);
  // Obligatoriefelet går före landsformatet – tomt fält ska inte få formatfel.
  const postalError =
    errors?.postalCode ?? postalCodeError(values.country, values.postalCode, locale);
  const hasCountry = Boolean(values.country.trim());

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <ComboboxField
        label={format.countryLabel}
        required
        value={values.country}
        onChange={(v) => onChange({ country: v, region: "", postalCode: "" })}
        options={countryList}
        placeholder={format.countryPlaceholder}
        searchPlaceholder={format.searchPlaceholder}
        error={errors?.country}
      />

      {hasCountry ? (
        <>
          <TextField
            label={format.addressLabel}
            required
            placeholder={format.addressPlaceholder}
            value={values.address}
            onChange={(v) => onChange({ address: v })}
            disabled={disabled ?? false}
            error={errors?.address}
          />
          <TextField
            label={format.postalLabel}
            required
            placeholder={format.postalPlaceholder}
            maxLength={format.postalMaxLength}
            value={values.postalCode}
            onChange={(v) => onChange({ postalCode: v })}
            error={postalError}
            disabled={disabled ?? false}
          />
          <TextField
            label={format.cityLabel}
            required
            value={values.city}
            onChange={(v) => onChange({ city: v })}
            disabled={disabled ?? false}
            error={errors?.city}
          />
          {format.regionHidden ? null : format.regionOptions ? (
            <ComboboxField
              label={format.regionLabel}
              required
              value={values.region}
              onChange={(v) => onChange({ region: v })}
              options={format.regionOptions}
              placeholder={
                locale === "en"
                  ? `Select ${format.regionLabel.toLowerCase()}`
                  : locale === "es"
                    ? `Selecciona ${format.regionLabel.toLowerCase()}`
                    : `Välj ${format.regionLabel.toLowerCase()}`
              }
              searchPlaceholder={locale === "es" ? "Buscar…" : locale === "en" ? "Search…" : "Sök…"}
              error={errors?.region}
            />
          ) : (
            <TextField
              label={format.regionLabel}
              value={values.region}
              onChange={(v) => onChange({ region: v })}
              disabled={disabled ?? false}
              error={errors?.region}
            />
          )}
        </>
      ) : null}
    </div>
  );
}
